import { Routes } from '@angular/router';
import { DroitClientFinal } from '@logaia_users/logaia-dto';
import { authGuard } from '@app/guards/auth.guard';
import { clientGuard } from '@app/guards/client.guard';
import { collabOnlyGuard, ecranReserveCollabGuard, gestionInterneGuard } from '@app/guards/collab.guard';
import { accesDossierGuard } from '@app/guards/acces-dossier.guard';
import { dossierDeTravailGuard } from '@app/guards/dossier-de-travail.guard';
import { dossierLibreGuard } from '@app/guards/dossier-libre.guard';
import { hasExerciceComptableGuard } from '@app/guards/exercice-comptable.guard';
import { modificationsEnCoursGuard } from '@app/guards/modifications-en-cours.guard';
import { chargementDossierResolver } from '@app/chargement-dossier.resolver';
import { DROITS_FACTURATION } from '@app/droits';
import { avecReprise } from '@app/chargement-ecran';
import { ecranClientOuvertAuCollabGuard, vueCollabGuard } from '@app/acces-complet';
import { horsArchiveGuard } from '@app/archive-situation';
import { routesDeclarationsCollab, routeTvaCollab } from './features/declarations/declarations.routes';

/*
 * Routes de l'application : connexion, pages collaborateur hors dossier, cadre d'un dossier et ses écrans, page
 * introuvable. Les droits d'un écran (data.droit) sont vérifiés par accesDossierGuard (core/droits.ts).
 * Chaque écran est chargé à la demande avec reprise automatique si son fichier ne se charge pas
 * (`avecReprise(() => import(...))`, core/chargement-ecran.ts).
 */

// #region Constantes et utilitaires des routes

/** Titre de l'onglet du navigateur, le même sur toutes les pages. */
const TITRE_APP = 'Logaia';

/**
 * Droits des onglets client / fournisseur (balances, grands livres), exigés en plus de _EDITION
 * (comme les onglets de ces écrans et le menu de l'ancien code).
 */
const DROITS_TIERS = {
  client: DroitClientFinal._CLIENT,
  fournisseur: DroitClientFinal._FOURNISSEUR,
};

/*
 * Routes collaborateur : déclarées seulement si l'accès collaborateur est compilé (`...(LOGAIA_COLLAB_BUILD ? [...] : [])`,
 * voir core/fonctionnalites.ts). Sinon le compilateur les écarte, avec leurs écrans (version « client seul »).
 * Elles restent protégées à l'exécution par vueCollabGuard / collabOnlyGuard (CollaborateurService.isCollab, faux
 * sans l'accès collaborateur) : un écran collaborateur d'un dossier s'ouvre au collaborateur et, en develop, au client avec accès
 * complet sur ce dossier quand le back le lui permet (core/acces-complet.ts) ; sinon la route suivante de même chemin
 * est essayée (canMatch).
 */

/**
 * Écrans collaborateur (routes à canMatch vueCollabGuard) tapés par un client qui n'y a pas accès : « Vous n'avez pas accès à cet écran. » et
 * accueil du dossier, au lieu de « Page introuvable ». Mêmes chemins que les routes données, sous-chemins compris.
 * Placées après toutes les routes du client de même préfixe (ex. fiche-societe/dossier-permanent).
 */
const refusAuClient = (routesCollab: Routes): Routes =>
  routesCollab.map((r) => ({
    path: r.path,
    canActivate: [ecranReserveCollabGuard],
    children: [{ path: '**', children: [] }],
  }));

/**
 * Suivi des clients et des fournisseurs : écrans du client, ouverts aussi au collaborateur en develop (décision de Claire
 * du 24/10) ; build « client seul » : réservés au client comme avant. Même garde pour le panier de paiement (« Liste des
 * règlements » du bureau, ouverte au collaborateur : reprise du WPF, 24/10).
 */
const GARDE_SUIVI_TIERS = LOGAIA_COLLAB_BUILD ? ecranClientOuvertAuCollabGuard : clientGuard;

const introuvable = () =>
  avecReprise(() => import('./layout/introuvable/introuvable.component')).then((c) => c.IntrouvableComponent);
// #endregion

// #region Routes collaborateur d'un dossier (absentes du build « client seul »)

/*
 * Routes collaborateur d'un dossier, par groupe (voir le commentaire ci-dessus) ; vides dans le build « client seul ».
 */
const ROUTES_COLLAB_TRESORERIE: Routes = LOGAIA_COLLAB_BUILD
  ? [
      {
        // Rapprochement bancaire et emprunts : collaborateur seulement (ancien canMatch isCollab).
        path: 'tresorerie',
        canMatch: [vueCollabGuard],
        data: { droit: DroitClientFinal._TRESORERIE },
        children: [
          {
            path: 'rapprochement-bancaires',
            loadComponent: () =>
              avecReprise(() => import('./features/tresorerie/rapprochement/rapprochement.component'))
                .then((c) => c.RapprochementComponent),
          },
          {
            // Bureau : grisé en archive (décision de Claire du 24/10 : refusé comme le bureau).
            path: 'emprunt',
            canActivate: [horsArchiveGuard],
            loadComponent: () => avecReprise(() => import('./features/tresorerie/emprunts/emprunts.component'))
              .then((c) => c.EmpruntsComponent),
          },
        ],
      },
    ]
  : [];

const ROUTES_COLLAB_FICHE_SOCIETE: Routes = LOGAIA_COLLAB_BUILD
  ? [
      {
        // Collaborateur : fiche société à onglets (coordonnées, banques, informations fiscales modifiables).
        // Le client ne la voit pas (canMatch) et garde la route du dossier permanent (fiche-societe/dossier-permanent).
        path: 'fiche-societe',
        canMatch: [vueCollabGuard],
        children: [
          { path: '', redirectTo: 'coordonnees', pathMatch: 'full' },
          {
            path: ':onglet',
            canDeactivate: [modificationsEnCoursGuard],
            loadComponent: () =>
              avecReprise(() => import('./features/fiche-societe/fiche-societe.component'))
                .then((c) => c.FicheSocieteComponent),
          },
        ],
      },
    ]
  : [];

/** Balance analytique (reprise du WPF, 24/10) : collaborateur seulement, placée avant balances/:type. */
const ROUTES_COLLAB_BALANCES: Routes = LOGAIA_COLLAB_BUILD
  ? [
      {
        path: 'balances/analytique',
        canMatch: [vueCollabGuard],
        data: { droit: DroitClientFinal._EDITION },
        loadComponent: () =>
          avecReprise(() => import('./features/compta/balances/balance-analytique.component')).then(
            (c) => c.BalanceAnalytiqueComponent,
          ),
      },
    ]
  : [];

const ROUTES_COLLAB_COMPTA: Routes = LOGAIA_COLLAB_BUILD
  ? [
      {
        // Collaborateur seulement ; garde de sortie : lot en cours non enregistré (comme l'ancien code).
        path: 'saisie-standard',
        canMatch: [vueCollabGuard],
        canDeactivate: [modificationsEnCoursGuard],
        data: { droit: DroitClientFinal._SAISIE },
        loadComponent: () =>
          avecReprise(() => import('./features/saisie/saisie-standard.component'))
            .then((c) => c.SaisieStandardComponent),
      },
      {
        // Saisie bancaire et saisie en situation (bureau SaisieType _BANCAIRE / _SITUATION) : même écran que la saisie
        // standard, mode donné par data.modeSaisie (features/saisie/saisie-mode.model.ts) ; même garde de sortie.
        path: 'saisie-bancaire',
        canMatch: [vueCollabGuard],
        canDeactivate: [modificationsEnCoursGuard],
        data: { droit: DroitClientFinal._SAISIE, modeSaisie: 'bancaire' },
        loadComponent: () =>
          avecReprise(() => import('./features/saisie/saisie-standard.component'))
            .then((c) => c.SaisieStandardComponent),
      },
      {
        path: 'saisie-situation',
        canMatch: [vueCollabGuard],
        canDeactivate: [modificationsEnCoursGuard],
        data: { droit: DroitClientFinal._SAISIE, modeSaisie: 'situation' },
        loadComponent: () =>
          avecReprise(() => import('./features/saisie/saisie-standard.component'))
            .then((c) => c.SaisieStandardComponent),
      },
      {
        // Révision de compte (?numeroCompte=&ecritureId=) : collaborateur seulement.
        path: 'revision',
        canMatch: [vueCollabGuard],
        loadComponent: () => avecReprise(() => import('./features/revision/revision.component'))
          .then((c) => c.RevisionComponent),
      },
      {
        // Journaux (consultation) du bureau : collaborateur seulement.
        path: 'journaux',
        canMatch: [vueCollabGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/compta/journaux/journaux-consultation.component'))
            .then((c) => c.JournauxConsultationComponent),
      },
    ]
  : [];

const ROUTES_COLLAB_DOSSIER: Routes = LOGAIA_COLLAB_BUILD
  ? [
      {
        // Centralisateur : accueil du collaborateur sous son propre titre, pour le client avec accès complet qui garde
        // l'accueil du client (menu Accueil › Centralisateur, décision de Claire du 24/10).
        path: 'centralisateur',
        canMatch: [vueCollabGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/dossier/accueil/collab/centralisateur-ecran.component'))
            .then((c) => c.CentralisateurEcranComponent),
      },
      {
        // Lettrage automatique du dossier (menu Outils du bureau) : collaborateur seulement, refusé en archive (bureau).
        path: 'outils/lettrage-automatique',
        canMatch: [vueCollabGuard],
        canActivate: [horsArchiveGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/compta/lettrage-auto/lettrage-auto.component'))
            .then((c) => c.LettrageAutoComponent),
      },
      {
        // Éléments de facturation du dossier (menu Outils du bureau, facturation du cabinet) : collaborateur seulement,
        // refusé en archive (bureau).
        path: 'outils/elements-facturation',
        canMatch: [vueCollabGuard],
        canActivate: [horsArchiveGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/gestion-interne/facturation/elements/elements-facturation.component'))
            .then((c) => c.ElementsFacturationComponent),
      },
      {
        // Autres outils du dossier (recalcul, contrôles, imports et exports traités par le back) : après les deux
        // routes ci-dessus (features/outils-dossier/outils-dossier.routes.ts).
        path: 'outils',
        canMatch: [vueCollabGuard],
        loadChildren: () =>
          avecReprise(() => import('./features/outils-dossier/outils-dossier.routes')).then((m) => m.routesOutilsDossier()),
      },
      {
        // Clôture de l'exercice ou de la situation ouverte (menu Archive et clôture) : refusée en archive (bureau) ;
        // confirmation pour quitter pendant la clôture.
        path: 'cloture',
        canMatch: [vueCollabGuard],
        canActivate: [horsArchiveGuard],
        canDeactivate: [modificationsEnCoursGuard],
        loadComponent: () => avecReprise(() => import('./features/cloture/cloture.component')).then((c) => c.ClotureComponent),
      },
      {
        // Création d'une situation (menu Situations).
        path: 'situations/nouvelle',
        canMatch: [vueCollabGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/situations/nouvelle-situation.component')).then((c) => c.NouvelleSituationComponent),
      },
      {
        // Recherche d'écriture : collaborateur seulement (ancien collabGuard).
        path: 'recherche-ecriture',
        canMatch: [vueCollabGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/recherche/recherche-ecriture.component'))
            .then((c) => c.RechercheEcritureComponent),
      },
      {
        // Immobilisations : collaborateur seulement ; exercice comptable requis (ancien hasExerciceComptableGuard).
        path: 'immobilisations',
        canMatch: [vueCollabGuard],
        // Bureau : grisé en archive (décision de Claire du 24/10).
        canActivate: [horsArchiveGuard, hasExerciceComptableGuard],
        data: { droit: DroitClientFinal._IMMOBILISATION },
        loadComponent: () =>
          avecReprise(() => import('./features/immobilisations/immobilisations.component'))
            .then((c) => c.ImmobilisationsComponent),
      },
      {
        // Crédit-bail : collaborateur seulement (pas d'écran dans l'ancien front, back CreditBailController).
        path: 'credit-bail',
        canMatch: [vueCollabGuard],
        // Bureau : grisé en archive (décision de Claire du 24/10).
        canActivate: [horsArchiveGuard],
        data: { droit: DroitClientFinal._IMMOBILISATION },
        loadComponent: () =>
          avecReprise(() => import('./features/immobilisations/credit-bail/credit-bail.component'))
            .then((c) => c.CreditBailComponent),
      },
      {
        // Dossier annuel : collaborateur seulement (ancien collabGuard + dossierDeTravailGuard).
        path: 'dossier-de-travail',
        canMatch: [vueCollabGuard],
        canActivate: [dossierDeTravailGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/dossier-annuel/dossier-annuel.component'))
            .then((c) => c.DossierAnnuelComponent),
      },
      {
        // Menu Révision (dossier de révision du bureau) : FNP, CCA, FAE, PCA, charges à payer, produits à recevoir,
        // contrôle TVA, intérêts C/C, comptes réciproques, liste des dossiers annuels (dossier-revision.routes.ts).
        path: 'revision',
        canMatch: [vueCollabGuard],
        loadChildren: () =>
          avecReprise(() => import('./features/dossier-revision/dossier-revision.routes')).then((m) => m.routesDossierRevision()),
      },
      // Déclarations et états financiers repris du bureau (features/declarations/declarations.routes.ts) : listes IS, CVAE,
      // PFU, Calcul TVS, saisie générique, documents des menus des états, historiques.
      ...routesDeclarationsCollab(),
      {
        // Menu Paramétrage du dossier : mêmes écrans que l'espace Paramétrage du cabinet, en portée dossier
        // (features/parametrage/parametrage-dossier.routes.ts).
        path: 'parametrage',
        canMatch: [vueCollabGuard],
        loadChildren: () =>
          avecReprise(() => import('./features/parametrage/parametrage-dossier.routes')).then((m) =>
            m.routesParametrageDossier(),
          ),
      },
      {
        // Suivi des factures et dépôts (validation des pièces) : collaborateur seulement, même chemin que l'ancien
        // (gestion-ocr, collabGuard). Garde de sortie : lot de l'écriture proposée en cours de saisie.
        path: 'gestion-ocr',
        canMatch: [vueCollabGuard],
        // Bureau : « Suivi Dépôts/OCR/FE » grisé en archive (décision de Claire du 24/10).
        canActivate: [horsArchiveGuard],
        canDeactivate: [modificationsEnCoursGuard],
        loadComponent: () =>
          avecReprise(() => import('./features/validation-pieces/validation-pieces.component'))
            .then((c) => c.ValidationPiecesComponent),
      },
    ]
  : [];

// #endregion

// #region Arbre des routes
export const routes: Routes = [
  { path: '', redirectTo: 'signin', pathMatch: 'full' },
  {
    path: 'signin',
    title: TITRE_APP,
    loadComponent: () =>
      avecReprise(() => import('./features/connexion/connexion.component')).then((c) => c.ConnexionComponent),
  },
  {
    path: '',
    canActivate: [authGuard],
    children: [
      ...(LOGAIA_COLLAB_BUILD ? ([{
        // Pages collaborateur hors dossier, dans le même cadre que les dossiers (pilules de l'en-tête).
        path: '',
        canActivate: [collabOnlyGuard],
        loadComponent: () =>
          avecReprise(() => import('./layout/cadre-collab/cadre-collab.component'))
            .then((c) => c.CadreCollabComponent),
        children: [
          {
            path: 'dossiers',
            title: TITRE_APP,
            // Page d'arrivée : pas de menu latéral (l'aide est dans l'en-tête).
            data: { sansMenu: true },
            loadComponent: () =>
              avecReprise(() => import('./features/collab/dossiers/dossiers.component'))
                .then((c) => c.DossiersComponent),
          },
          // Espaces Gestion interne (droit Gestion interne), Paramétrage et Outils du cabinet : écrans de portée cabinet
          // décrits dans layout/cadre-collab/espaces-collab.model.ts (menu, titres, droits), routes chargées à la demande.
          {
            path: 'gestion-interne',
            title: TITRE_APP,
            canActivate: [gestionInterneGuard],
            loadChildren: () =>
              avecReprise(() => import('./layout/cadre-collab/espaces-collab.routes')).then((m) =>
                m.routesEspace('gestion-interne'),
              ),
          },
          {
            path: 'parametrage',
            title: TITRE_APP,
            loadChildren: () =>
              avecReprise(() => import('./layout/cadre-collab/espaces-collab.routes')).then((m) =>
                m.routesEspace('parametrage'),
              ),
          },
          {
            path: 'outils',
            title: TITRE_APP,
            loadChildren: () =>
              avecReprise(() => import('./layout/cadre-collab/espaces-collab.routes')).then((m) =>
                m.routesEspace('outils'),
              ),
          },
        ],
      }] satisfies Routes) : []),
      {
        path: 'dossier/:id',
        // Accès du client au dossier de l'URL vérifié avant tout appel au back.
        canActivate: [accesDossierGuard, dossierLibreGuard],
        canActivateChild: [accesDossierGuard],
        loadComponent: () =>
          avecReprise(() => import('./layout/cadre-dossier/cadre-dossier.component')).then(
            (c) => c.CadreDossierComponent,
          ),
        resolve: { dossier: chargementDossierResolver },
        children: [
          {
            path: 'accueil',
            loadComponent: () =>
              avecReprise(() => import('./features/dossier/accueil/accueil.component')).then(
                (c) => c.AccueilComponent,
              ),
          },
          {
            path: 'facturation',
            // Même droit que le menu (facturation simple ou complète), vérifié par accesDossierGuard (l'ancien code
            // ne contrôlait que le menu : écrans ouverts par adresse tapée).
            data: { droit: DROITS_FACTURATION },
            children: [
              {
                path: 'factures',
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/factures/factures.component')).then(
                    (c) => c.FacturesComponent,
                  ),
              },
              {
                path: 'devis',
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/devis/devis.component')).then(
                    (c) => c.DevisComponent,
                  ),
              },
              {
                path: 'article-facturations',
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/articles/articles.component')).then(
                    (c) => c.ArticlesComponent,
                  ),
              },
              {
                // Plans de facturation vers les clients du dossier (bureau : Paramétrage › Facturation client) : facturation
                // complète seulement (même règle que le menu et l'aide, core/droits estFacturationComplete).
                path: 'plans',
                data: { facturationComplete: true, titre: 'Plan de facturation', icon: 'receipt' },
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/plans/plans.component')).then((c) => c.PlansComponent),
              },
              {
                path: 'lancement',
                data: { facturationComplete: true, titre: 'Lancer plans facturation', icon: 'receipt' },
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/lancement/lancement.component')).then(
                    (c) => c.LancementComponent,
                  ),
              },
              {
                // Destinataires (menu collaborateur) : ouvert au collaborateur qui travaille à la place de son client
                // (décision de Claire, 20/10 ; l'ancien code le réservait au client).
                path: 'clients',
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/clients/clients.component')).then(
                    (c) => c.ClientsComponent,
                  ),
              },
              {
                // Ouvert au collaborateur (décision de Claire, 20/10) : il y voit en plus les champs réservés au cabinet.
                path: 'emetteur',
                canDeactivate: [modificationsEnCoursGuard],
                loadComponent: () =>
                  avecReprise(() => import('./features/facturation/emetteur/emetteur.component')).then(
                    (c) => c.EmetteurComponent,
                  ),
              },
            ],
          },
          {
            // Ancien code : droit porté seulement par le menu et la carte Trésorerie ; vérifié ici aussi.
            path: 'transactions',
            data: { droit: DroitClientFinal._TRESORERIE },
            loadComponent: () =>
              avecReprise(() => import('./features/tresorerie/transactions/transactions.component')).then(
                (c) => c.TransactionsComponent,
              ),
          },
          ...ROUTES_COLLAB_TRESORERIE,
          {
            path: 'piece-comptables',
            // Ouvert aux collaborateurs (bouton « Historique des dépôts »), sans clientGuard ; client : sans droit
            // particulier, comme le dépôt de pièces (décision de Claire du 03/11).
            loadComponent: () =>
              avecReprise(() => import('./features/pieces/pieces.component')).then((c) => c.PiecesComponent),
          },
          {
            // Sans droit particulier (comme l'ancien front) : accessible même sans le droit fournisseur.
            path: 'facture-electroniques',
            canActivate: [clientGuard],
            loadComponent: () =>
              avecReprise(() => import('./features/suivi-factures/suivi-factures.component')).then((c) => c.SuiviFacturesComponent),
          },
          {
            path: 'facture-a-payer',
            canActivate: [GARDE_SUIVI_TIERS],
            data: { droit: DroitClientFinal._FOURNISSEUR },
            loadComponent: () =>
              avecReprise(() => import('./features/fournisseurs/panier/panier.component'))
                .then((c) => c.PanierComponent),
          },
          {
            path: 'clients',
            canActivate: [GARDE_SUIVI_TIERS],
            data: { droit: DroitClientFinal._CLIENT, tiers: 'client' },
            loadComponent: () => avecReprise(() => import('./features/suivi-tiers/suivi-tiers.component'))
              .then((c) => c.SuiviTiersComponent),
          },
          {
            path: 'fournisseurs',
            canActivate: [GARDE_SUIVI_TIERS],
            data: { droit: DroitClientFinal._FOURNISSEUR, tiers: 'fournisseur' },
            loadComponent: () => avecReprise(() => import('./features/suivi-tiers/suivi-tiers.component'))
              .then((c) => c.SuiviTiersComponent),
          },
          // Collaborateur et client avec accès complet : liste TVA reprise du bureau (avant la route du client).
          ...(LOGAIA_COLLAB_BUILD ? routeTvaCollab() : []),
          {
            path: 'declarations',
            // Comme l'ancien code : pas de garde, seulement le droit.
            data: { droit: DroitClientFinal._DECLARATION },
            loadComponent: () => avecReprise(() => import('./features/tva/tva.component')).then((c) => c.TvaComponent),
          },
          ...ROUTES_COLLAB_FICHE_SOCIETE,
          {
            path: 'fiche-societe/dossier-permanent',
            // Droit vérifié par accesDossierGuard (canActivateChild), comme l'ancien code.
            data: { droit: DroitClientFinal._DP },
            loadComponent: () =>
              avecReprise(() => import('./features/documents/documents.component')).then((c) => c.DocumentsComponent),
          },
          {
            path: 'comptabilite-generale',
            children: [
              // Balance analytique (collaborateur) : avant balances/:type.
              ...ROUTES_COLLAB_BALANCES,
              ...refusAuClient(ROUTES_COLLAB_BALANCES),
              {
                // Avant balances/:type.
                path: 'balances/exploitation',
                data: { droit: DroitClientFinal._EDITION },
                loadComponent: () =>
                  avecReprise(() => import('./features/compta/balances/balance-exploitation.component')).then(
                    (c) => c.BalanceExploitationComponent,
                  ),
              },
              {
                path: 'balances/:type',
                data: { droit: DroitClientFinal._EDITION, droitSelonType: DROITS_TIERS },
                loadComponent: () =>
                  avecReprise(() => import('./features/compta/balances/balances.component'))
                    .then((c) => c.BalancesComponent),
              },
              {
                path: 'grand-livre/:type',
                data: { droit: DroitClientFinal._EDITION, droitSelonType: DROITS_TIERS },
                loadComponent: () =>
                  avecReprise(() => import('./features/compta/grand-livre/grand-livre.component')).then(
                    (c) => c.GrandLivreComponent,
                  ),
              },
              ...ROUTES_COLLAB_COMPTA,
              ...refusAuClient(ROUTES_COLLAB_COMPTA),
            ],
          },
          ...ROUTES_COLLAB_DOSSIER,
          // Écrans collaborateur tapés par un client : refus, puis accueil du dossier.
          ...refusAuClient([...ROUTES_COLLAB_TRESORERIE, ...ROUTES_COLLAB_FICHE_SOCIETE, ...ROUTES_COLLAB_DOSSIER]),
          { path: '', redirectTo: 'accueil', pathMatch: 'full' },
          // Adresse inconnue dans un dossier : page introuvable, dans le cadre du dossier.
          { path: '**', loadComponent: introuvable },
        ],
      },
    ],
  },
  {
    path: 'not-found',
    title: TITRE_APP,
    loadComponent: introuvable,
  },
  { path: '**', redirectTo: 'not-found' },
];
// #endregion
